import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useRemoveOutOfStock, useUpdateOutOfStock } from '../../../apis/products-api';
import { formatReturnDateOption, OutOfStockItem } from '../OutOfStockUtils';
import {
  ItemTypeChip,
  ScheduleDateField,
  ScheduleDateOption,
  useExternalShopId,
  useOutOfStockPermissions,
  useScheduleDates,
} from '../OutOfStockShared';
import { Dialog } from '../../../design-system/Dialog';
import { useCustomSnackBar } from '../../../shared/SnackBar/useCustomSnackBar';
import { getProductsApiError } from '../../../apis/products-api.errors';

type Props = {
  /** The row being edited; `null` keeps the dialog closed. */
  item: OutOfStockItem | null;
  onClose: () => void;
};

export const EditOutOfStockItemModal: React.FC<Props> = ({ item, onClose }) => {
  const { t } = useTranslation();
  const [date, setDate] = useState<string>('');
  // Which button is busy, rather than the mutations' isLoading: both handlers fire one mutateAsync per
  // grouped id through Promise.all, and the mutation observer only reflects the most recent of them.
  const [pendingAction, setPendingAction] = useState<'save' | 'restore' | null>(null);
  // `pendingAction` still says which button is busy, but the guard reads this ref: state updates land a
  // render later, so two fast clicks would otherwise both get past an `isBusy` check.
  const isBusyRef = useRef(false);

  const snackBar = useCustomSnackBar();
  const externalShopId = useExternalShopId();
  const { today, dateOptions: selectableDates } = useScheduleDates();
  const updateOutOfStock = useUpdateOutOfStock();
  const removeOutOfStock = useRemoveOutOfStock();

  const { canUpdate: userAuthorizesToUpdateOutOfStock } = useOutOfStockPermissions();

  const open = Boolean(item);
  const isBusy = pendingAction !== null;

  // The item's current return date can sit outside the next 7 days on offer, so surface it as an extra
  // leading option instead of silently re-dating the item to whatever the first option happens to be.
  const dateOptions = useMemo(() => {
    if (!item?.scheduledUntil) return selectableDates;

    const scheduled = dayjs(item.scheduledUntil);
    const alreadyIncluded = selectableDates.some((option) => dayjs(option.value).isSame(scheduled, 'day'));
    if (alreadyIncluded) return selectableDates;

    const scheduledOption: ScheduleDateOption = {
      label: formatReturnDateOption(scheduled),
      value: scheduled.format(),
      duration: scheduled.diff(today, 'day'),
    };
    return [scheduledOption, ...selectableDates];
  }, [selectableDates, item, today]);

  // Preselect the item's existing return date each time the dialog opens.
  useEffect(() => {
    if (!open) return;

    const scheduled = item?.scheduledUntil ? dayjs(item.scheduledUntil) : undefined;
    const matchingOption = scheduled
      ? dateOptions.find((option) => dayjs(option.value).isSame(scheduled, 'day'))
      : undefined;
    setDate(matchingOption?.value ?? dateOptions[0]?.value ?? '');
  }, [open, item, dateOptions]);

  // Shared bookkeeping around both mutations: mark which one is running and surface API errors. The
  // list refreshes itself — both mutation hooks invalidate the out-of-stock query on success.
  const submit = async (action: 'save' | 'restore', run: () => Promise<unknown>, successMessage: string) => {
    isBusyRef.current = true;
    setPendingAction(action);
    try {
      await run();
      snackBar.showSuccess(successMessage);
      onClose();
    } catch (e) {
      snackBar.showError(getProductsApiError(e).message);
    } finally {
      isBusyRef.current = false;
      setPendingAction(null);
    }
  };

  // Both handlers mutate every grouped id, not just the row's own itemId: several ingredients can share
  // a warehouse item and were merged into this single row.
  const handleSave = async () => {
    const current = item;
    if (!current || !externalShopId || !userAuthorizesToUpdateOutOfStock || isBusyRef.current) return;

    const scheduledUntil = dayjs(date).toISOString();
    await submit(
      'save',
      () =>
        Promise.all(
          current.groupedItemIds.map((itemId) =>
            updateOutOfStock.mutateAsync({
              ...(current.type === 'ingredient' ? { ingredientId: itemId } : { productId: itemId }),
              externalShopId,
              scheduledUntil,
            })
          )
        ),
      t('global.saveSucceeded')
    );
  };

  const handleReturnToMenu = async () => {
    const current = item;
    if (!current || !externalShopId || !userAuthorizesToUpdateOutOfStock || isBusyRef.current) return;

    await submit(
      'restore',
      () =>
        Promise.all(
          current.groupedItemIds.map((itemId) =>
            removeOutOfStock.mutateAsync(
              current.type === 'ingredient'
                ? { ingredientId: itemId, externalShopId }
                : { productId: itemId, externalShopId }
            )
          )
        ),
      t('Returned to sale')
    );
  };

  const canUpdate = userAuthorizesToUpdateOutOfStock && !isBusy;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <>
          {/* component="span": DialogTitle already renders the heading element. */}
          <Typography variant="h3" component="span">
            {item?.name}
          </Typography>
          {item && <ItemTypeChip type={item.type} />}
        </>
      }
      actions={
        <>
          <Button onClick={onClose} color="inherit">
            {t('global.cancel')}
          </Button>
          {/* Separate loading flags: only the button that was pressed spins, the other just disables. */}
          <LoadingButton
            disabled={!canUpdate || !date}
            loading={pendingAction === 'save'}
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            {t('Save')}
          </LoadingButton>
        </>
      }
    >
      <ScheduleDateField value={date} options={dateOptions} onChange={setDate} disabled={!canUpdate} />

      {/* A different outcome from Save rather than a peer of it: it ignores the chosen date entirely. */}
      <RestoreButton
        disabled={!canUpdate}
        loading={pendingAction === 'restore'}
        variant="outlined"
        color="primary"
        onClick={handleReturnToMenu}
      >
        {t('Return to sale')}
      </RestoreButton>
    </Dialog>
  );
};

const RestoreButton = styled(LoadingButton)`
  && {
    align-self: flex-start;
    width: 100%;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      width: auto;
    }
  }
`;

export default EditOutOfStockItemModal;
